import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Frown } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { PERSONAS } from "../../content/site";

type Accent = "teal" | "violet";

const ACCENT: Record<
  Accent,
  { wash: string; gainCard: string; icon: string }
> = {
  teal: {
    wash: "bg-teal-mist",
    gainCard: "bg-teal-mist",
    icon: "text-teal",
  },
  violet: {
    wash: "bg-violet-mist",
    gainCard: "bg-violet-mist",
    icon: "text-violet",
  },
};

export function Personas() {
  const [active, setActive] = useState<number>(0);
  const persona = PERSONAS[active];
  const accent = ACCENT[persona.accent];

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % PERSONAS.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (i - 1 + PERSONAS.length) % PERSONAS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = PERSONAS.length - 1;
    else return;
    e.preventDefault();
    setActive(next);
    document.getElementById(`persona-tab-${PERSONAS[next].id}`)?.focus();
  };

  return (
    <section id="personas" className="relative overflow-hidden bg-cream py-20 sm:py-28">
      {/* faint dotted texture behind the deck */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-grid-dots opacity-60 [background-size:26px_26px]"
      />

      <div className="container-x relative">
        <SectionHeading
          eyebrow="Made for where you are"
          title={
            <>
              You've been let down by an app{" "}
              <span className="marker">before</span>
            </>
          }
          sub="Three kinds of people are done with trackers that ignore their progress. FitBuddy is built for all three."
        />

        {/* Tab bar */}
        <Reveal delay={1} className="mt-12">
          <div
            role="tablist"
            aria-label="Choose the person that sounds most like you"
            className="flex flex-wrap justify-center gap-3 sm:gap-4"
          >
            {PERSONAS.map((p, i) => {
              const selected = i === active;
              return (
                <button
                  key={p.id}
                  role="tab"
                  type="button"
                  id={`persona-tab-${p.id}`}
                  aria-selected={selected}
                  aria-controls={`persona-panel-${p.id}`}
                  tabIndex={selected ? 0 : -1}
                  onKeyDown={(e) => onTabKey(e, i)}
                  onClick={() => setActive(i)}
                  className={`cursor-pointer rounded-full px-5 py-2.5 font-display text-sm font-extrabold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 focus-visible:ring-offset-cream sm:px-6 sm:py-3 sm:text-base ${
                    selected
                      ? "border-[3px] border-navy bg-navy text-white shadow-pop-sm"
                      : "border-[3px] border-navy bg-white text-navy shadow-pop-sm hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none active:translate-x-[4px] active:translate-y-[4px]"
                  }`}
                >
                  {p.tab}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Profile-card deck */}
        <Reveal delay={2} className="mt-12">
          <div className="relative mx-auto max-w-4xl">
            {/* the rest of the "deck" peeking out behind the active card */}
            <div
              aria-hidden
              className="absolute inset-x-8 -bottom-4 top-8 rotate-2 rounded-[1.75rem] border-[3px] border-navy bg-sun-pale"
            />
            <div
              aria-hidden
              className="absolute inset-x-4 -bottom-2 top-4 -rotate-1 rounded-[1.75rem] border-[3px] border-navy bg-white"
            />

            <div className="relative min-h-[34rem] sm:min-h-[30rem]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={persona.id}
                  id={`persona-panel-${persona.id}`}
                  role="tabpanel"
                  aria-labelledby={`persona-tab-${persona.id}`}
                  initial={{ opacity: 0, y: 18, rotate: 1.5 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  exit={{ opacity: 0, y: -18, rotate: -1.5 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="card-pop relative grid gap-8 p-6 sm:p-8 md:grid-cols-[0.9fr_1.1fr] md:p-10"
                >
                  {/* corner sticker, decorative echo of the active tab */}
                  <span
                    aria-hidden
                    className="sticker absolute -top-4 left-5 z-10 -rotate-3 sm:left-8"
                  >
                    {persona.tab}
                  </span>

                  {/* Real photo on a bold pastel wash */}
                  <div
                    className={`relative flex items-stretch justify-center rounded-[1.5rem] p-4 sm:p-5 ${accent.wash}`}
                  >
                    <img
                      src={persona.photo}
                      alt=""
                      aria-hidden
                      className="min-h-[14rem] w-full -rotate-2 rounded-[1.5rem] border-[3px] border-navy object-cover shadow-pop-sm"
                    />
                  </div>

                  <div className="flex flex-col justify-center">
                    <h3 className="display text-3xl sm:text-4xl text-navy">
                      {persona.headline}
                    </h3>

                    <div className="mt-6 flex flex-col items-stretch">
                      {/* Before */}
                      <div className="rounded-[1.25rem] border-2 border-dashed border-navy/30 bg-navy/[0.04] p-5">
                        <div className="flex items-center gap-2">
                          <Frown
                            className="h-5 w-5 text-charcoal/50"
                            strokeWidth={2}
                          />
                          <span className="font-display text-xs font-extrabold uppercase tracking-wider text-charcoal/50">
                            Before
                          </span>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-charcoal/65 sm:text-base">
                          {persona.pain}
                        </p>
                      </div>

                      {/* Connecting arrow */}
                      <div className="relative z-10 -my-2 flex justify-center" aria-hidden>
                        <motion.div
                          animate={{ y: [0, 5, 0] }}
                          transition={{
                            duration: 1.4,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-navy bg-sun shadow-pop-xs"
                        >
                          <ArrowDown className="h-5 w-5 text-navy" strokeWidth={3} />
                        </motion.div>
                      </div>

                      {/* After */}
                      <div
                        className={`rounded-[1.25rem] border-[3px] border-navy p-5 pt-6 shadow-pop-sm ${accent.gainCard}`}
                      >
                        <div className="flex items-center gap-2">
                          <ArrowUpRight
                            className={`h-5 w-5 ${accent.icon}`}
                            strokeWidth={3}
                          />
                          <span className="font-display text-xs font-extrabold uppercase tracking-wider text-navy">
                            After
                          </span>
                        </div>
                        <p className="mt-3 text-sm font-semibold leading-relaxed text-navy sm:text-base">
                          {persona.gain}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
